import React, { useCallback, useMemo, useState } from "react";

import { Empty, Table, message } from "antd";

import Swal from "sweetalert2";

import LdasApi from "../../../../apis/Ldas.api";
import { LDAS_DELETE_SWAL_CONFIG } from "../LdasUtils";

import LdasValidityAddModal from "./LdasValidityAddModal";

const getCertColumns = ({ onCertEdit, onCertDelete }) => [
  {
    title: "인증기관",
    dataIndex: "certBody",
    key: "certBody",
    render: (value) => (
      <span className="ldas-product-name-cell__text">{value}</span>
    ),
  },
  {
    title: "취득일",
    dataIndex: "certDate",
    key: "certDate",
    render: (value) => <span className="ldas-cell-date">{value ?? "-"}</span>,
  },
  {
    title: "관리",
    key: "actions",
    align: "center",
    render: (_, record) => (
      <div className="ldas-actions-cell">
        <button
          type="button"
          className="ldas-action-btn"
          onClick={() => onCertEdit(record)}
          aria-label="인증 수정"
        >
          수정
        </button>
        <button
          type="button"
          className="ldas-action-btn ldas-action-btn--danger"
          onClick={() => onCertDelete(record)}
          aria-label="인증 삭제 확인"
        >
          삭제
        </button>
      </div>
    ),
  },
];

const LdasLotDetailValiditySection = ({
  certs,
  certCount,
  lotId,
  onCertMerged,
  onRefetch,
  pagination,
}) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);

  const handleAddModalClose = useCallback(() => {
    setEditingCert(null);
    setAddModalOpen(false);
  }, []);

  const handleCertEdit = useCallback((cert) => {
    setEditingCert(cert);
    setAddModalOpen(true);
  }, []);

  // 인증 삭제
  const handleCertDelete = useCallback(
    (cert) => {
      const label =
        String(cert?.agency ?? cert?.certBody ?? "").trim() || "이 인증";
      Swal.fire({
        ...LDAS_DELETE_SWAL_CONFIG,
        title: `"${label}" 인증을 삭제할까요?`,
        text: "정말로 이 인증을 삭제하시겠습니까?",
      }).then(async (result) => {
        if (!result.isConfirmed) return;
        try {
          const res = await LdasApi.deleteLdasCertification(cert.id);
          console.log("deleteLdasCertification res", res);
          if (res?.code !== 200) {
            message.error(res?.message ?? "인증을 삭제할 수 없습니다");
            return;
          }
          await onRefetch();
        } catch (e) {
          console.error("deleteLdasCertification", e);
          message.error("인증을 삭제할 수 없습니다");
        }
      });
    },
    [onRefetch],
  );

  // 인증 추가
  const handleCertCreate = useCallback(
    async (certData) => {
      try {
        const param = {
          lotId: Number(lotId),
          certBody: certData.certBody,
          certDate: certData.certDate,
        };
        console.log("createLdasCertification param", param);
        const res = await LdasApi.createLdasCertification(param);
        console.log("createLdasCertification res", res);
        if (res?.code !== 200) {
          message.error(res?.message ?? "인증을 추가할 수 없습니다");
          return;
        }
        const saved = res?.result;
        if (saved && saved.id !== undefined && saved.id !== null) {
          onCertMerged(saved);
        } else {
          await onRefetch();
        }
        handleAddModalClose();
      } catch (e) {
        console.error("createLdasCertification", e);
        message.error("인증을 추가할 수 없습니다");
      }
    },
    [lotId, onCertMerged, onRefetch, handleAddModalClose],
  );

  // 인증 수정
  const handleCertUpdate = useCallback(
    async (certData) => {
      try {
        const param = {
          certBody: certData.certBody,
          certDate: certData.certDate,
          result: "PASS",
        };
        console.log("updateLdasCertification param", param);
        const res = await LdasApi.updateLdasCertification(
          Number(certData.id),
          param,
        );
        console.log("updateLdasCertification res", res);
        if (res?.code !== 200) {
          message.error(res?.message ?? "인증을 수정할 수 없습니다");
          return;
        }
        if (res?.result && res?.code === 200) {
          onCertMerged(res?.result);
        } else {
          await onRefetch();
        }
        handleAddModalClose();
      } catch (e) {
        console.error("updateLdasCertification", e);
        message.error("인증을 수정할 수 없습니다");
      }
    },
    [onCertMerged, onRefetch, handleAddModalClose],
  );

  const columns = useMemo(
    () =>
      getCertColumns({
        onCertEdit: handleCertEdit,
        onCertDelete: handleCertDelete,
      }),
    [handleCertEdit, handleCertDelete],
  );

  return (
    <>
      <div className="ldas-detail-section-card">
        <div className="ldas-detail-section-card__head">
          <div className="ldas-detail-section-card__title-wrap">
            <span className="ldas-detail-section-card__title">
              ✅ 인증 목록
            </span>
            <span className="ldas-count-badge ldas-count-badge--active">
              {certCount ?? certs.length}
            </span>
          </div>
          <button
            type="button"
            className="ldas-detail-lot-add-btn"
            onClick={() => {
              setEditingCert(null);
              setAddModalOpen(true);
            }}
          >
            + 인증 추가
          </button>
        </div>

        <div className="ldas-table-card ldas-table-card--borderless ldas-detail-lot-table">
          <Table
            columns={columns}
            dataSource={certs}
            rowKey="id"
            pagination={pagination ?? false}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="등록된 인증이 없습니다"
                />
              ),
            }}
            size="middle"
            components={{
              header: {
                cell: ({ children, className, style, ...rest }) => (
                  <th
                    {...rest}
                    className={["ldas-table-header-cell", className]
                      .filter(Boolean)
                      .join(" ")}
                    style={style}
                  >
                    {children}
                  </th>
                ),
              },
            }}
          />
        </div>
      </div>

      <LdasValidityAddModal
        visible={addModalOpen}
        editingCert={editingCert}
        onCancel={handleAddModalClose}
        onSave={editingCert ? handleCertUpdate : handleCertCreate}
      />
    </>
  );
};

export default LdasLotDetailValiditySection;
